import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, IconButton,
  Tooltip, Chip, Button, Divider,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RateReviewIcon from '@mui/icons-material/RateReview';

interface PendingBand {
  _id: string;
  artistName: string;
  mainImage: string | null;
  genres: string[];
  createdAt: string;
  createdBy: { _id: string; name: string; email: string } | null;
}

const timeAgo = (dateString?: string) => {
  if (!dateString) return '';
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? '' : 's'} ago`;
};

const shadow = '0 1px 4px rgba(0,0,0,0.95), 0 2px 8px rgba(0,0,0,0.8)';

const PendingBandsWidget: React.FC = () => {
  const navigate = useNavigate();
  const [bands, setBands] = useState<PendingBand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/bands/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBands(res.data);
      } catch {
        // silently fail — widget shouldn't break the dashboard
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading || bands.length === 0) return null;

  const PREVIEW_COUNT = 3;
  const preview = bands.slice(0, PREVIEW_COUNT);
  const remainder = bands.length - PREVIEW_COUNT;

  return (
    <Paper
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'warning.main',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PendingActionsIcon sx={{ color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Pending Bands</Typography>
          <Chip label={bands.length} size="small" color="warning" sx={{ fontWeight: 700 }} />
        </Box>
        <Button component={Link} to="/admin/pending-bands" size="small" variant="outlined" color="warning">
          Review All
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {preview.map(band => (
          <Box
            key={band._id}
            onClick={() => window.open(`/band/${band._id}`, '_blank')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 1,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 72,
              ...(band.mainImage ? {
                backgroundImage: `linear-gradient(to left, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 20%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0) 70%), url(${band.mainImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              } : {
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }),
              transition: 'all 0.15s',
            }}
          >
            {!band.mainImage && (
              <Box sx={{
                width: 40, height: 40, borderRadius: 1, flexShrink: 0,
                bgcolor: 'action.disabledBackground',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MusicNoteIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
              </Box>
            )}

            {/* Name + submitter */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'white', textShadow: shadow }} noWrap>
                {band.artistName}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', textShadow: shadow }} noWrap>
                {band.createdBy?.name ?? 'Unknown artist'}
                {band.genres.length > 0 && ` · ${band.genres.slice(0, 2).join(', ')}`}
              </Typography>
            </Box>

            {/* Time ago */}
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', textShadow: shadow, flexShrink: 0 }}>
              {timeAgo(band.createdAt)}
            </Typography>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
              <Tooltip title="Open band page">
                <IconButton size="small" component="a" href={`/band/${band._id}`} target="_blank" sx={{ color: 'white' }}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Go to review page">
                <IconButton size="small" onClick={() => navigate('/admin/pending-bands')} sx={{ color: 'white' }}>
                  <RateReviewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      {remainder > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button component={Link} to="/admin/pending-bands" size="small" color="warning">
            + {remainder} more pending band{remainder > 1 ? 's'  : ''}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PendingBandsWidget;
